import { Page, View, Image, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import bgImage from '@assets/images/bg_speakers.png';
import {
  Portfolio,
  PublicEventDetailsResponse,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import { APP_HOST } from '@utils/apiHooks';
import { SafeImage } from '../../components/SafeImage';
import { useMemo } from 'react';

export interface ColocatedEventsProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  header: JSX.Element;
  footer: JSX.Element;
}

const ColocatedEvents = ({
  event,
  coLocatedEvents,
  portfolios,
  header,
  footer,
}: ColocatedEventsProps) => {
  const { t } = useTranslation();

  const firstMatch = useMemo(() => {
    const testimonials = event?.website_content?.testimonials;

    if (!testimonials?.length) return null;

    const valid = testimonials
      .filter(
        t => t?.first_name && t?.last_name && t?.citation && t?.company_name
      )
      .sort((a, b) => a.order - b.order);

    if (!valid.length) return null;

    const findByPriority = (key: 'is_partner' | 'is_home' | 'is_whoandwhy') =>
      valid.find(t => t[key]);

    const testimonial =
      findByPriority('is_partner') ||
      findByPriority('is_home') ||
      findByPriority('is_whoandwhy');

    if (!testimonial) return null;

    return {
      name: [testimonial.first_name, testimonial.last_name]
        .filter(Boolean)
        .join(' '),
      jobTitle: testimonial.job_title,
      companyName: testimonial.company_name,
      citation: testimonial.citation,
    };
  }, [event?.website_content?.testimonials]);

  const selectedTestimonials = useMemo(() => {
    const testimonials = event?.website_content?.testimonials;

    if (!testimonials?.length) return [];

    const valid = testimonials
      .filter(
        t => t?.first_name && t?.last_name && t?.citation && t?.company_name
      )
      .sort((a, b) => a.order - b.order);

    if (!valid.length) return [];

    const prioritized = [
      ...valid.filter(t => t.is_partner),
      ...valid.filter(t => !t.is_partner && t.is_home),
      ...valid.filter(t => !t.is_partner && !t.is_home && t.is_whoandwhy),
    ];

    // Remove testimonial already used on previous page
    const filtered = firstMatch
      ? prioritized.filter(
          t =>
            t.citation !== firstMatch.citation &&
            t.job_title !== firstMatch.jobTitle &&
            t.company_name !== firstMatch.companyName
        )
      : prioritized;

    if (!filtered.length) return [];

    // Take first 3
    const topThree = filtered.slice(0, 3);

    // Map to final structure
    return topThree.map(t => ({
      id: t.id,
      name: [t.first_name, t.last_name].filter(Boolean).join(' '),
      jobTitle: t.job_title,
      companyName: t.company_name,
      citation: t.citation,
    }));
  }, [event?.website_content?.testimonials, firstMatch]);

  const partnerPackagesLink = `${APP_HOST}/events/${event?.slug}/business-partner.pdf`;

  const renderListTitle = () => {
    const maximizeText = t('partner-maximize-reach', {
      eventName: event?.name,
    });
    return maximizeText;
  };

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.backgroundContainer}>
        <SafeImage
          src={
            'https://media.we-conect.com/images/pdf/agenda/pdf_agenda_colocated.jpg'
          }
          style={styles.backgroundImage}
          debugLabel="agenda-colocated-background"
        />

        <View style={styles.backgroundOverlay} />

        <View>{header}</View>

        <View style={styles.overlayContent}>
          <Text style={styles.title}>{t('co-located-page-title-part-1')}</Text>
          <Text style={styles.title}>
            {t('co-located-page-title-part-2', {
              numberOfColocatedEvents: coLocatedEvents?.length ?? 1,
            })}
          </Text>
          <View
            style={{
              ...styles.lineBar,
            }}
          ></View>
        </View>
      </View>

      <View
        style={{
          ...styles.contentWrapper,
        }}
      >
        <Text style={styles.listTitle}>{renderListTitle()}</Text>

        {[1, 2, 3, 4, 5].map(index => {
          return (
            <View
              key={`increase-your-sales-item-${index}`}
              style={styles.itemWrapper}
            >
              <Text style={styles.bulletItem}>
                {'\u2022'} {t(`co-located-page-list-item-${index}`)}
              </Text>
            </View>
          );
        })}

        <View style={styles.inlineTextWrapper}>
          <Text>
            <Text style={styles.listSubParagraphBold}>
              {t('co-located-page-list-subparagraph-bold')}{' '}
            </Text>
            <Text style={styles.listSubParagraph}>
              {t('co-located-page-list-subparagraph')}
            </Text>
          </Text>
        </View>

        <View style={styles.buttonWrapper}>
          <Link
            style={{
              ...styles.button,
              backgroundColor: event?.design?.themeColor,
            }}
            src={partnerPackagesLink}
          >
            {t('bp.button')}
          </Link>
        </View>
      </View>

      {selectedTestimonials?.length > 0 && (
        <View style={styles.testimonialsWrapper}>
          {selectedTestimonials?.map((t, index) => (
            <View key={index} style={styles.testimonialBox}>
              <Text style={styles.testimonialCitation}>{t.citation}</Text>
              <Text style={styles.testimonialSpeaker}>
                {t.jobTitle}, {t.companyName}
              </Text>
            </View>
          ))}
        </View>
      )}

      {footer}
    </Page>
  );
};

export default ColocatedEvents;
